import React from 'react';
import { Group, Select, type ComboboxItem } from '@mantine/core';
import {
  isRecruiterLanguage,
  RECRUITER_LANGUAGE_OPTIONS,
  type RecruiterLanguage,
  type RecruiterLanguageOption,
} from 'recruiter-i18n/languages';

import FlagEmoji from '../FlagEmoji';

// Select values are strings, so the null option needs a stand-in.
const NULL_OPTION_VALUE = 'none';

const findLanguageOption = (
  code: string | null
): RecruiterLanguageOption | undefined =>
  RECRUITER_LANGUAGE_OPTIONS.find((option) => option.code === code);

const renderOption = ({ option }: { option: ComboboxItem }) => {
  const language = findLanguageOption(option.value);
  if (!language) return option.label;
  return (
    <Group gap='xs'>
      <FlagEmoji emoji={language.emoji} countryCode={language.countryCode} />
      <span>{language.label}</span>
    </Group>
  );
};

interface RecruiterLanguageSelectProps {
  label: string;
  /**
   * Label of a first option that stands for no language (null); the caller
   * decides what that falls back to. Without it, a language is required.
   */
  nullOptionLabel?: string;
  value: RecruiterLanguage | null;
  onChange: (value: RecruiterLanguage | null) => void;
  disabled?: boolean;
  /** Disable the portal when embedded in another popup, such as the support menu. */
  withinPortal?: boolean;
}

/** A recruiter display-language select. Languages show their flag. */
export function RecruiterLanguageSelect({
  label,
  nullOptionLabel,
  value,
  onChange,
  disabled,
  withinPortal = true,
}: RecruiterLanguageSelectProps) {
  const selectedOption = findLanguageOption(value);
  return (
    <Select
      label={label}
      data={[
        ...(nullOptionLabel === undefined
          ? []
          : [{ value: NULL_OPTION_VALUE, label: nullOptionLabel }]),
        ...RECRUITER_LANGUAGE_OPTIONS.map((option) => ({
          value: option.code,
          label: option.label,
        })),
      ]}
      value={value ?? NULL_OPTION_VALUE}
      onChange={(next) => {
        if (next === NULL_OPTION_VALUE) onChange(null);
        else if (isRecruiterLanguage(next)) onChange(next);
      }}
      allowDeselect={false}
      renderOption={renderOption}
      leftSection={
        selectedOption ? (
          <FlagEmoji
            emoji={selectedOption.emoji}
            countryCode={selectedOption.countryCode}
          />
        ) : null
      }
      disabled={disabled}
      comboboxProps={{ withinPortal }}
    />
  );
}
